import type { ComponentType, ReactNode } from 'react';

export interface PageTab {
  key: string;
  label: string;
  /**
   * How many records sit behind this tab, rendered as a pill after the label. Optional —
   * omit (or pass null while the count is still loading) and the tab is label-only, which
   * is what the tab strips that predate counts render.
   */
  count?: number | null;
  /** A warning pill after the label ("82 failed") — something on that tab needs a look. */
  alert?: string | null;
  /** An optional icon before the label (heroicons outline, 24px set). */
  icon?: ComponentType<{ className?: string }>;
}

export interface PageTabsProps {
  tabs: PageTab[];
  active: string;
  onChange: (key: string) => void;
  className?: string;
  /** The open tab's own tools, on the right of the strip (they drop below it on a narrow screen). */
  actions?: ReactNode;
}

/**
 * Underlined tab strip for pages that merge two surfaces (User Management,
 * Attendance Config). The tabs scroll sideways (no visible scrollbar) rather than wrap.
 */
export default function PageTabs({ tabs, active, onChange, className = '', actions }: PageTabsProps) {
  if (tabs.length < 2) return null;
  return (
    <div className={`flex flex-wrap items-end gap-x-4 border-b border-border ${className}`}>
      <div role="tablist" className="flex min-w-0 max-w-full gap-1 overflow-x-auto [scrollbar-width:none] [&::-webkit-scrollbar]:hidden">
      {tabs.map((t) => {
        const isActive = active === t.key;
        return (
          <button
            key={t.key}
            type="button"
            role="tab"
            aria-selected={isActive}
            onClick={() => onChange(t.key)}
            className={`inline-flex shrink-0 items-center gap-1.5 whitespace-nowrap rounded-t-md border-b-2 px-3 py-2.5 text-sm transition-colors focus:outline-none focus-visible:bg-muted ${
              isActive
                ? 'border-brand text-foreground font-medium'
                : 'border-transparent text-muted-foreground hover:text-foreground'
            }`}
          >
            {t.icon && <t.icon className="h-4 w-4 shrink-0" />}
            {t.label}
            {t.count != null && (
              <span
                className={`inline-flex min-w-[1.5rem] justify-center rounded-full px-1.5 py-0.5 text-[11px] font-semibold leading-none tabular-nums ${
                  isActive ? 'bg-brand-light text-brand' : 'bg-muted text-muted-foreground'
                }`}
              >
                {t.count}
              </span>
            )}
            {t.alert && (
              <span className="inline-flex rounded-full bg-red-500/10 px-2 py-0.5 text-[11px] font-semibold leading-none text-red-600">{t.alert}</span>
            )}
          </button>
        );
      })}
      </div>
      {actions && <div className="ml-auto flex flex-wrap items-center justify-end gap-2 py-1.5">{actions}</div>}
    </div>
  );
}
